import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme
} from '@mui/material';
import { LineChart } from '@mui/x-charts';
import DashboardLayout from '../../layouts/DashboardLayout';
import { getUSDRates, getEURates } from '../../api/currencyRates';

const CurrencyVisualizerScreen = () => {
  const theme = useTheme();
  const [usdData, setUsdData] = useState([]);
  const [eurData, setEurData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState(30); // days

  const fetchCurrencyData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [usdResponse, eurResponse] = await Promise.all([
        getUSDRates({ limit: timeRange }),
        getEURates({ limit: timeRange })
      ]);

      // Process USD data
      const usdRates = usdResponse.data
        .map(item => ({
          date: new Date(item.effectiveDate),
          rate: parseFloat(item.rate)
        }))
        .sort((a, b) => a.date - b.date);

      // Process EUR data
      const eurRates = eurResponse.data
        .map(item => ({
          date: new Date(item.effectiveDate),
          rate: parseFloat(item.rate)
        }))
        .sort((a, b) => a.date - b.date);

      setUsdData(usdRates);
      setEurData(eurRates);
    } catch (err) {
      console.error('Error fetching currency data:', err);
      setError('Error al cargar los datos de tipos de cambio');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrencyData();
  }, [timeRange]);

  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Box p={3}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Box p={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Visualizador de Tipos de Cambio
          </Typography>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Período</InputLabel>
            <Select
              value={timeRange}
              label="Período"
              onChange={handleTimeRangeChange}
            >
              <MenuItem value={7}>7 días</MenuItem>
              <MenuItem value={15}>15 días</MenuItem>
              <MenuItem value={30}>30 días</MenuItem>
              <MenuItem value={60}>60 días</MenuItem>
              <MenuItem value={90}>90 días</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Grid container spacing={3}>
          {/* USD Chart */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 400 }}>
              <Typography variant="h6" gutterBottom align="center">
                USD/MXN
              </Typography>
              {usdData.length > 0 ? (
                <LineChart
                  xAxis={[
                    {
                      data: usdData.map(item => item.date),
                      scaleType: 'time',
                      label: 'Fecha',
                    },
                  ]}
                  yAxis={[
                    {
                      label: 'Tipo de Cambio (MXN)',
                      valueFormatter: (value) => 
                        new Intl.NumberFormat('es-MX', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 4,
                        }).format(value),
                    },
                  ]}
                  series={[
                    {
                      data: usdData.map(item => item.rate),
                      label: 'USD/MXN',
                      color: theme.palette.primary.main,
                      showMark: true,
                      curve: 'linear',
                    },
                  ]}
                  height={300}
                  margin={{ top: 20, right: 20, bottom: 50, left: 80 }}
                />
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="300px">
                  <Typography color="text.secondary">
                    No hay datos disponibles para USD
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* EUR Chart */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 400 }}>
              <Typography variant="h6" gutterBottom align="center">
                EUR/MXN
              </Typography>
              {eurData.length > 0 ? (
                <LineChart
                  xAxis={[
                    {
                      data: eurData.map(item => item.date),
                      scaleType: 'time',
                      label: 'Fecha',
                    },
                  ]}
                  yAxis={[
                    {
                      label: 'Tipo de Cambio (MXN)',
                      valueFormatter: (value) => 
                        new Intl.NumberFormat('es-MX', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 4,
                        }).format(value),
                    },
                  ]}
                  series={[
                    {
                      data: eurData.map(item => item.rate),
                      label: 'EUR/MXN',
                      color: theme.palette.secondary.main,
                      showMark: true,
                      curve: 'linear',
                    },
                  ]}
                  height={300}
                  margin={{ top: 20, right: 20, bottom: 50, left: 80 }}
                />
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="300px">
                  <Typography color="text.secondary">
                    No hay datos disponibles para EUR
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Summary Cards */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Resumen USD/MXN
                  </Typography>
                  {usdData.length > 0 ? (
                    <Box>
                      <Typography>
                        <strong>Último valor:</strong> {usdData[usdData.length - 1]?.rate.toFixed(4)} MXN
                      </Typography>
                      <Typography>
                        <strong>Valor más alto:</strong> {Math.max(...usdData.map(d => d.rate)).toFixed(4)} MXN
                      </Typography>
                      <Typography>
                        <strong>Valor más bajo:</strong> {Math.min(...usdData.map(d => d.rate)).toFixed(4)} MXN
                      </Typography>
                      <Typography>
                        <strong>Variación:</strong> {usdData.length > 1 ? 
                          ((usdData[usdData.length - 1]?.rate - usdData[0]?.rate) / usdData[0]?.rate * 100).toFixed(2) + '%' 
                          : 'N/A'}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography color="text.secondary">No hay datos disponibles</Typography>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Resumen EUR/MXN
                  </Typography>
                  {eurData.length > 0 ? (
                    <Box>
                      <Typography>
                        <strong>Último valor:</strong> {eurData[eurData.length - 1]?.rate.toFixed(4)} MXN
                      </Typography>
                      <Typography>
                        <strong>Valor más alto:</strong> {Math.max(...eurData.map(d => d.rate)).toFixed(4)} MXN
                      </Typography>
                      <Typography>
                        <strong>Valor más bajo:</strong> {Math.min(...eurData.map(d => d.rate)).toFixed(4)} MXN
                      </Typography>
                      <Typography>
                        <strong>Variación:</strong> {eurData.length > 1 ? 
                          ((eurData[eurData.length - 1]?.rate - eurData[0]?.rate) / eurData[0]?.rate * 100).toFixed(2) + '%' 
                          : 'N/A'}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography color="text.secondary">No hay datos disponibles</Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </DashboardLayout>
  );
};

export default CurrencyVisualizerScreen;
